import { Add, Close, Send } from '@mui/icons-material';
import { Button, Box, Typography, MenuItem } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import {
  Page,
  PageTitleWithButton,
  Table,
  Pagination,
  Modal,
  ModalSaveSection,
  Loader,
  SMBSelect
} from '../../../../components';
import { PendingVendorsModal } from './pending-vendors-model';
import { IError, IPendingVendor, IResponse, IVendorType } from '../../../../models';
import {
  getPendingVendors,
  rejectPendingVendor,
  sendPendingVendorReminder,
  approvePendingVendor,
  getVendorTypes
} from '../../../../fetch';
import { formatInputPhoneNumber, unCamelCase } from '../../../../helpers';

export const PendingVendors = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isRejecting, setIsRejecting] = useState<{
    [id: number]: boolean;
  }>({});
  const [isSending, setIsSending] = useState<{
    [id: number]: boolean;
  }>({});
  const [isApproving, setIsApproving] = useState(false);
  const [isVendorModalOpen, showVendorModal] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [pendingVendorId, setPendingVendorId] = useState<string | number | null>(null);
  const [selectedVendorTypeId, setSelectedVendorTypeId] = useState<string | number>('');

  const {
    isLoading: isLoadingPendingVendors,
    data,
    refetch: refetchPendingVendors
  } = useQuery<IResponse<IPendingVendor[]>, IError>(
    ['pending-vendors', page, perPage],
    () =>
      getPendingVendors({
        page: page + 1,
        perPage
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      onError: d => {
        enqueueSnackbar(d?.Detail ?? 'Error loading pending vendors', {
          variant: 'error'
        });
      },
      notifyOnChangeProps: 'tracked'
    }
  );
  const pendingVendors = useMemo(() => data?.records || [], [data]);

  const { isLoading: isLoadingVendorTypes, data: vendorTypesData } = useQuery<
    IResponse<IVendorType[]>,
    IError
  >(
    ['vendorTypes'],
    () =>
      getVendorTypes({
        hasSageId: true
      }),
    {
      onError: d => {
        enqueueSnackbar(d?.Detail ?? 'Error loading pending vendors', {
          variant: 'error'
        });
      }
    }
  );
  const vendorTypes = useMemo(() => vendorTypesData?.records || [], [vendorTypesData]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Vendor Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Phone #',
        accessor: 'phoneNumber',
        sort: false,
        Cell: props => {
          return (
            <>
              {props?.cell?.row?.original?.pendingVendorDocuments?.[0]?.contactPhone
                ? formatInputPhoneNumber(
                    props?.cell?.row?.original?.pendingVendorDocuments?.[0]?.contactPhone
                  )
                : ''}
            </>
          );
        }
      },
      {
        Header: 'Email',
        accessor: 'email',
        sort: false,
        Cell: props => {
          return <>{props?.cell?.row?.original?.pendingVendorDocuments?.[0]?.contactEmail ?? ''}</>;
        }
      },
      {
        Header: 'Contact Name',
        accessor: 'contactName',
        sort: false,
        Cell: props => {
          return <>{props?.cell?.row?.original?.pendingVendorDocuments?.[0]?.contactName ?? ''}</>;
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPendingVendor } };
        }) => {
          return (
            <Box display="flex" justifyContent="center" gap="8px">
              <LoadingButton
                id="edit"
                color="secondary"
                startIcon={<Send />}
                loading={isSending[original.pendingVendorId]}
                loadingPosition="start"
                onClick={async () => {
                  try {
                    setIsSending({
                      [original.pendingVendorId]: true
                    });
                    await sendPendingVendorReminder(original.pendingVendorId);
                    enqueueSnackbar('Email sent to pending vendor!', {
                      variant: 'success'
                    });
                  } catch (error) {
                    enqueueSnackbar(error?.Detail ?? 'Error sending email to pending vendor', {
                      variant: 'error'
                    });
                  } finally {
                    setIsSending({
                      [original.pendingVendorId]: false
                    });
                  }
                }}
              >
                Send Email
              </LoadingButton>
              <LoadingButton
                loading={isRejecting[original.pendingVendorId]}
                loadingPosition="start"
                color="error"
                startIcon={<Close />}
                onClick={async () => {
                  const result = window.confirm('Are you sure you want to reject this vendor?');
                  if (result) {
                    try {
                      setIsRejecting({
                        [original.pendingVendorId]: true
                      });
                      await rejectPendingVendor(original.pendingVendorId);
                      enqueueSnackbar('Rejected pending vendor!', {
                        variant: 'success'
                      });
                      refetchPendingVendors();
                    } catch (error) {
                      enqueueSnackbar(error?.Detail ?? 'Error rejecting pending vendor', {
                        variant: 'error'
                      });
                    } finally {
                      setIsRejecting({
                        [original.pendingVendorId]: false
                      });
                    }
                  }
                }}
              >
                Reject
              </LoadingButton>
              <Button
                color="primary"
                startIcon={<Close />}
                onClick={() => {
                  setPendingVendorId(original.pendingVendorId);
                }}
              >
                Approve
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRejecting, refetchPendingVendors, isSending]);

  return (
    <>
      <Page title="Pending Vendors">
        <PageTitleWithButton
          pageTitleBackProps={{
            title: 'Pending Vendors',
            backText: 'Back to Admin home',
            backUrl: '/admin'
          }}
          button={
            <Button
              startIcon={<Add />}
              color="primary"
              variant="contained"
              onClick={() => showVendorModal(true)}
            >
              Add Pending Vendor
            </Button>
          }
        />
        <Table
          columns={columns}
          data={pendingVendors}
          isLoading={isLoadingPendingVendors}
          hidePagination
        />
        {!isLoadingPendingVendors && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </Page>
      <PendingVendorsModal
        open={isVendorModalOpen}
        handleClose={(shouldRefresh?: boolean) => {
          if (shouldRefresh) {
            refetchPendingVendors();
          }
          showVendorModal(false);
        }}
      />
      <Modal
        open={!!pendingVendorId}
        onClose={() => {
          setPendingVendorId(null);
          setSelectedVendorTypeId('');
        }}
        title="Approve Pending Vendor"
        maxWidth="xs"
      >
        <Box mt={1}>
          {isApproving && <Loader type="overlay" position="centered" />}

          <SMBSelect
            disabled={isApproving || isLoadingVendorTypes}
            label="Vendor Type"
            value={selectedVendorTypeId}
            onChange={({ target: { value } }) => setSelectedVendorTypeId(value)}
            handleClear={() => setSelectedVendorTypeId('')}
          >
            {vendorTypes &&
              vendorTypes.map((partnerType, index) => {
                return (
                  <MenuItem key={`${index}`} value={partnerType.vendorTypeId}>
                    {unCamelCase(partnerType.name)}
                  </MenuItem>
                );
              })}
          </SMBSelect>
          <Typography gutterBottom variant="caption">
            Select a vendor type to assign to the pending vendor.
          </Typography>
          <ModalSaveSection
            handleCancel={() => {
              setPendingVendorId(null);
              setSelectedVendorTypeId('');
            }}
            isSaveDisabled={!selectedVendorTypeId || isApproving}
            handleSave={async () => {
              try {
                setIsApproving(true);
                await approvePendingVendor(pendingVendorId, {
                  vendorTypeId: selectedVendorTypeId
                });
                enqueueSnackbar('Vendor Approved!', {
                  variant: 'success'
                });
                setPendingVendorId(null);
                setSelectedVendorTypeId(null);
                refetchPendingVendors();
              } catch (error) {
                enqueueSnackbar(error?.Detail ?? 'Error approving pending vendor', {
                  variant: 'error'
                });
              } finally {
                setIsApproving(false);
              }
            }}
          />
        </Box>
      </Modal>
    </>
  );
};
